import { Providers } from "@microsoft/mgt-element";

export interface AuthenticatedFetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
}

/**
 * Utility function to make authenticated API requests using Entra ID bearer tokens.
 * Automatically adds the Authorization header with a valid bearer token.
 * 
 * @param url - The API endpoint URL
 * @param options - Fetch options (method, body, headers, etc.)
 * @returns Promise that resolves to the Response object
 * @throws Error if authentication fails
 */
export async function authenticatedFetch(
  url: string, 
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  const accessToken = await Providers.globalProvider.getAccessToken();

  if (!accessToken) {
    throw new Error('Failed to obtain access token for API request');
  }

  // Merge headers with authorization
  // For FormData, we must let the browser set Content-Type with the boundary parameter
  // Using Headers API allows browser to auto-set Content-Type for FormData
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);
  
  // Only set Content-Type: application/json if not FormData and not already set
  if (!(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Make the authenticated request
  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Utility function to make authenticated API requests and automatically parse JSON response.
 * 
 * @param url - The API endpoint URL
 * @param options - Fetch options (method, body, headers, etc.)
 * @returns Promise that resolves to the parsed JSON response
 * @throws Error if authentication fails or response is not ok
 */
export async function authenticatedFetchJson<T = any>(
  url: string, 
  options: AuthenticatedFetchOptions = {}
): Promise<T> {
  const response = await authenticatedFetch(url, options);
  
  if (!response.ok) {
    // Try to get error details from response body
    let errorDetails = '';
    try {
      const errorBody = await response.text();
      errorDetails = errorBody ? ` - ${errorBody.substring(0, 200)}` : '';
      console.error(`[authenticatedFetch] Error response body:`, errorBody);
    } catch (e) {
      console.error(`[authenticatedFetch] Could not read error response body`);
    }
    
    const errorMessage = `API request failed: ${response.status} ${response.statusText}${errorDetails}`;
    console.error(`[authenticatedFetch] ${errorMessage}`, { url, status: response.status });
    throw new Error(errorMessage);
  }
  
  return response.json();
}
